import { checkIsInternal } from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import {
  SUREST_FILTER_API_URL,
  SUREST_TIME_PERIOD_API_URL,
  SUREST_RELATIVE_GRAPH_API_URL,
  SUREST_RELATIVE_COST_DRIVERS,
  SUREST_RELATIVE_DISTRIBUTION,
  SUREST_RELATIVE_COMMON_SERVICES,
} from '@src/middleware/services/surest/apiURLConstants';
import { extractLocationOptions } from '@src/apps/Surest/Main/locationFilterUtils';

let _httpService = new HttpService();

interface SurestRequestPayload {
  ProviderSysKey?: string;
  PricingBasis?: string;
  providerSysKey?: string;
  pricingBasis?: string;
  PeriodStart?: string;
  periodStart?: string;
  PeriodEnd?: string;
  periodEnd?: string;
  pandoraProviderKeys?: string[];
  pandoraProviderKey?: string;
  npi?: string;
  tin?: string;
  [key: string]: unknown;
}

const configureHttpService = () => {
  _httpService = new HttpService();
  if (checkIsInternal()) {
    _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
  } else {
    _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
  }
};

export const getSurestFilterData = async (payload: SurestRequestPayload) => {
  configureHttpService();
  const url = SUREST_FILTER_API_URL;
  const surestFilter: Record<string, unknown> = {
    ProviderSysKey: payload.providerSysKey || '',
    PricingBasis: payload.pricingBasis || 'location',
    PeriodStart: payload.PeriodStart || payload.periodStart || '',
    PeriodEnd: payload.PeriodEnd || payload.periodEnd || '',
  };
  const requestBody = {
    RequestedJourney: 'surestFilter',
    SurestFilter: surestFilter,
  };

  if (payload.pricingBasis === 'location' && payload.pandoraProviderKeys?.length > 1) {
    requestBody.SurestFilter['PandoraProviderKeys'] = payload.pandoraProviderKeys;
  } else if (payload.pricingBasis === 'location' && payload.pandoraProviderKey) {
    requestBody.SurestFilter['PandoraProviderKey'] = payload.pandoraProviderKey;
  } else if (payload.pricingBasis === 'office' && payload.npi) {
    requestBody.SurestFilter['Npi'] = payload.npi;
  }
  if (payload.tin) {
    requestBody.SurestFilter['Tin'] = payload.tin;
  }
  const responseData = await _httpService.push(url, requestBody);
  if (
    payload.pricingBasis === 'location' &&
    responseData &&
    !Array.isArray(responseData) &&
    Array.isArray(responseData.LocationOptions)
  ) {
    return {
      ...responseData,
      LocationOptions: responseData.LocationOptions.map((option) => ({
        title: option?.title || '',
        value: option?.value || '',
      })),
    };
  }

  if (responseData && Array.isArray(responseData)) {
    if (payload.pricingBasis === 'location') {
      const locationOptions = extractLocationOptions(responseData);
      if (locationOptions.length > 0) {
        return locationOptions;
      }

      return responseData.map((item) => {
        if (item.Locations && item.Locations.length > 0) {
          return {
            title: item.Locations[0].DisplayName || '',
            value: item.Locations[0].PandoraProviderKey || '',
          };
        }
        return item;
      });
    } else if (payload.pricingBasis === 'office') {
      return responseData.map((item) => {
        if (item.NpiDetails && item.NpiDetails.length > 0) {
          return {
            title: item.NpiDetails[0].NpiName || '',
            value: item.NpiDetails[0].NpiNumber || '',
          };
        }
        return item;
      });
    }
  }

  return responseData;
};

export const getSurestTimePeriodData = async (_payload: SurestRequestPayload = {}) => {
  configureHttpService();
  return _httpService.get(SUREST_TIME_PERIOD_API_URL);
};

export const getSurestRelativeGraphData = async (payload: SurestRequestPayload) => {
  configureHttpService();
  const responseData = await _httpService.push(
    `${SUREST_RELATIVE_GRAPH_API_URL}?PricingBasis=${payload.PricingBasis}`,
    payload
  );
  return responseData;
};
export const getSurestRelativeDistributionData = async (payload: SurestRequestPayload) => {
  configureHttpService();
  const responseData = await _httpService.push(
    `${SUREST_RELATIVE_DISTRIBUTION}?PricingBasis=${payload.PricingBasis}`,
    payload
  );
  return responseData;
};

export const getSurestRelativeCommonServices = async (payload: SurestRequestPayload) => {
  configureHttpService();
  return _httpService.push(
    `${SUREST_RELATIVE_COMMON_SERVICES}?PricingBasis=${payload.PricingBasis}`,
    payload
  );
};

export const getSurestRelativeCostDrivers = async (payload: SurestRequestPayload = {}) => {
  try {
    configureHttpService();
    const responseData = await _httpService.push(
      `${SUREST_RELATIVE_COST_DRIVERS}?PricingBasis=${payload.PricingBasis}`,
      payload
    );
    return responseData;
  } catch {
    return null;
  }
};
